/**
 * @class msysMessage
 * @description
 * Provides functions for de/serialization of Mosy messages from/to binary format
 *
 * Binary format in file contains of binary block with following structure:
 * byte 0: size of block [3 bytes]
 * byte 3: version [1 byte]
 * byte 4: header size [1 byte]
 * byte 5: header encoded using msgpack [header size bytes]
 *  byte 6: 0x94 (message pack array with 4 elements) [1 byte]
 *  byte 7: 0xCF (follows uint64) [1 byte]
 *  byte 8-15: event_dt [8 bytes]
 *  byte 16: 0b101nnnnn: string with size nnnnn
 *  byte 17-17+nnnnn
 * [byte 5+header size bytes]: params encoded using msgpack [block_size - 2 - header_size bytes]
 */

const base = require('./base.js')

if(typeof unow==='undefined') {
  // eslint-disable-next-line no-global-assign
  if(typeof performance==='undefined') performance = require('perf_hooks').performance
  const MOSY_PROCESS_START_US = Date.now()*1000 - Math.round(performance.now()*1000)
  unow = function() { return MOSY_PROCESS_START_US+Math.round(performance.now()*1000) }
}
const msgpack = (function() {
  try{ return require('@msgpack/msgpack') }catch(e) {
    console.error(e)
  }
}())
const schema_actions = (function() {
  try{ return require('../../MosyMessageActions.json') }catch(e) {
    console.error(e)
  }
}())

const ms = module.exports = {}
ms.FORMAT_VERSION = 1
// current format is: 3 bytes block size, 1 bytes version, 1 byte header size, [event_dt,action,sender,target] (msgpack), params (msgpack)

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Serializes Mosy message to binary form
 * @param {object} msg
 * @param {block_length_included} whether length of the block shall be included at the start of output
 */
ms.msg2uint = function(msg, block_length_included) {
  if(!msgpack || !schema_actions) return null
  if(typeof msg==='string') msg = base.string2msg(msg)
  if(!msg || typeof msg!=='object') return null
  const params = msg.params && typeof msg.params==='object' ? { ...msg.params } : {}
  const event_dt = params.event_dt>0 ? params.event_dt: unow()
  delete params.event_dt
  const action_id = ms.action2id(msg.action)
  const sender_id = ms.address2id(msg.sender)
  const target_id = ms.address2id(msg.target)
  if(!action_id) return null
  try{
    const header_uint = msgpack.encode([event_dt, action_id, sender_id, target_id])
    const params_uint = ms.params2uint(msg.action, params)
    if(header_uint.length>255) return null
    const size = 2 + header_uint.length + params_uint.length
    const block_size_uint = block_length_included ? ms.size2block(size) : new Uint8Array(0)
    const uint = new Uint8Array(size + block_size_uint.length)
    let offset = 0
    uint.set(block_size_uint, offset)
    offset += block_size_uint.length
    uint[offset++] = ms.FORMAT_VERSION
    uint[offset++] = header_uint.length
    uint.set(header_uint, offset)
    offset += header_uint.length
    if(params_uint.length>0) { uint.set(params_uint, offset) }
    return uint
  }catch(e) {
    return null
  }
} // msg2uint

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Returns block size as Uint8Array used at the beginning of blocks
 * @param {integer} size
 * @returns {Uint8Array} - 3 bytes Uint8Array
 */
ms.size2block = function(size) {
  size = parseInt(size)
  size = size > 0 ? size : 0
  return new Uint8Array([size>>16, (size>>8)&0xFF, size & 0xFF])
} // size2block

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Deserializes Mosy message from binary form
 * @param {Uint8Array} uint
 * @param {block_length_included} whether length of the block is included at the start of output; if not, byteLength of uint is used
 */
ms.uint2msg = function(uint, block_length_included) {
  if(!msgpack || !schema_actions) return null
  try{
    const block_size = block_length_included ? (uint[0]<<16)+(uint[1]<<8)+uint[2] : uint.length
    let offset = block_length_included ? 3 : 0
    const version = uint[offset++]
    if(version!==1) return null
    const header_size = uint[offset++]
    const header_uint = uint.slice(offset, offset+header_size)
    offset += header_size
    const params_size = block_size - 2 - header_size
    const params_uint = uint.slice(offset, offset + params_size)
    const [event_dt, action_id, sender_id, target_id] = msgpack.decode(header_uint)
    const action = ms.id2action(action_id)
    const params = ms.uint2params(action, params_uint)
    if(!(event_dt>0)) return null
    params.event_dt = event_dt
    if(!action) return null
    const sender = ms.id2address(sender_id)
    const target = ms.id2address(target_id)
    return { sender, target, action, params }
  }catch(e) {
    return null
  }
} // uint2msg

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Obtain event_dt and action from binary message
 * @param {Uint8Array} uint
 * @param {boolean} block_length_included - whether length of the block is included at the start of output; if not, byteLength of uint is used
 */
ms.uint2dtaction = function(uint, block_length_included) {
  if(!msgpack || !schema_actions) return []
  try{
    let offset = block_length_included ? 3 : 0
    const version = uint[offset++]
    if(version!==1) return []
    const header_size = uint[offset++]
    const header_uint = uint.slice(offset, offset+header_size)
    const [event_dt, action_id] = msgpack.decode(header_uint)
    const action = ms.id2action(action_id)
    if(!action) return []
    return [event_dt, action, action_id]
  }catch(e) {
    return []
  }
} // uint2dtaction

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Obtain event_dt and action from binary message
 * @param {Uint8Array} uint
 * @param {boolean} block_length_included - whether length of the block is included at the start of output; if not, byteLength of uint is used
 */
ms.uint2dtactionid = function(uint, block_length_included) {
  if(!msgpack || !schema_actions) return []
  try{
    let offset = block_length_included ? 3 : 0
    const version = uint[offset++]
    if(version!==1) return []
    const header_size = uint[offset++]
    const header_uint = uint.slice(offset, offset+header_size)
    const [event_dt, action_id] = msgpack.decode(header_uint)
    return [event_dt, action_id]
  }catch(e) {
    return []
  }
} // uint2dtaction

ms.action2id = function(action) {
  if(typeof action !== 'string' || action.length===0) return null
  return action
} // action2id

ms.id2action = function(id) {
  if(typeof id==='number') {
    return id
  }else if(typeof id==='string' && id.length>0) {
    // fallback; action has no defined ID
    return id
  }else{
    return null
  }
} // id2action

ms.address2id = function(address) {
  if(address===null || typeof address==='undefined') {
    address = ''
  }else if(typeof address==='object') {
    address = base.address2string(address)
  }else if(typeof address==='number' && address>=1) {
    address = String(Math.floor(address))
  }else if(typeof address!=='string') {
    return null
  }
  const id = address==='' ? 0 : address
  return id
} // address2id

ms.id2address = function(id) {
  if(typeof id==='number') {
    const address = id===0 ? '' : String(id)
    return base.address2hash(address)
  }else if(typeof id==='string' && id.length>0) {
    // fallback; address has no defined ID
    return base.address2hash(id)
  }else{
    return null
  }
} // id2address

ms.params2uint = function(action, params) {
  if(!params || typeof params!=='object' || Object.keys(params).length===0) return new Uint8Array(0)
  delete params._meta
  try{
    return msgpack.encode(params)
  }catch(e) {
    return new Uint8Array(0)
  }
} // params2uint

ms.uint2params = function(action, uint) {
  if(!(uint instanceof Uint8Array)) return {}
  try{
    const params = msgpack.decode(uint) || {}
    if(action==='mmH264' && ('block_id' in params)) {
      params.id = params.block_id
      delete params.block_id
    }
    return params
  }catch(e) {
    return {}
  }
} // uint2params

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Reads messages from binary memory block
 */
ms.readMemoryMessages = function(buf, filter_function) {
  if(typeof buf==='string') {
    const half = Math.floor(buf.length / 2)
    const buf2 = new Uint8Array(half)
    for(let i=0; i<half; i++) {
      buf2[i] = Number.parseInt(buf.substr(i<<1, 2), 16)
    }
    buf = buf2
  }else{
    buf = new Uint8Array(buf)
  }
  const messages = []
  if(!buf?.buffer) return []
  const len = buf.length
  const len4 = len-4 // message must have at least 4 bytes
  for(let i=0; i<=len4;) {
    const size = (buf[i]<<16) + (buf[i+1]<<8) + buf[i+2]
    if(i+3+size>len) break // incomplete last block
    const msg = ms.uint2msg(buf.subarray(i+3, i+3+size))
    if(msg) {
      if(!filter_function || filter_function(msg.sender, msg.target, msg.action, msg.params)) { messages.push(msg) }
    }
    i += 3+size
  }
  return messages
} // readMemoryMessages
